import { z } from "zod";
import type { Args, Operation } from "../../types";
import { scoped } from "../../types";
import {
  paginatedSchema,
  paginationQuery,
  type PaginatedResult,
} from "../_common";
import {
  crmIdParams,
  crmLeadRowSchema,
  smartViewQuerySchema,
} from "./_shared";

const leadsListQuery = paginationQuery.extend({
  smart_view_slug: z.string().optional(),
  search: z.string().optional(),
});

const leadsListResponse = paginatedSchema(crmLeadRowSchema);
type LeadsListResponse = PaginatedResult<z.infer<typeof crmLeadRowSchema>>;
export type { LeadsListResponse };

const list: Operation<
  void,
  z.infer<typeof leadsListQuery>,
  void,
  z.infer<typeof leadsListResponse>
> = {
  method: "GET",
  path: () => "/api/crm/leads",
  query: leadsListQuery,
  response: leadsListResponse,
};

const get: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof crmLeadRowSchema>
> = {
  method: "GET",
  path: ({ id }) => `/api/crm/leads/${id}`,
  params: crmIdParams,
  response: crmLeadRowSchema,
};

const contactsList: Operation<
  { id: number },
  void,
  void,
  {
    contacts: { person_id: number; display_name: string; is_primary: boolean }[];
  }
> = {
  method: "GET",
  path: ({ id }) => `/api/crm/leads/${id}/contacts`,
  params: crmIdParams,
  response: z.object({
    contacts: z.array(
      z.object({
        person_id: z.number(),
        display_name: z.string(),
        is_primary: z.boolean(),
      }),
    ),
  }),
};

const create: Operation<
  void,
  void,
  {
    display_name: string;
    primary_person_id?: number | null;
    primary_organization_id?: number | null;
    status_slug?: string | null;
    source_slug?: string | null;
    owner_actor_id?: number | null;
  },
  z.infer<typeof crmLeadRowSchema>
> = {
  method: "POST",
  path: () => "/api/crm/leads",
  body: z.object({
    display_name: z.string().min(1),
    primary_person_id: z.number().nullable().optional(),
    primary_organization_id: z.number().nullable().optional(),
    status_slug: z.string().nullable().optional(),
    source_slug: z.string().nullable().optional(),
    owner_actor_id: z.number().nullable().optional(),
  }),
  response: crmLeadRowSchema,
  // Dropped revalidatePath("/work"), "/" — list is the lead data those pages rendered.
  invalidates: [list],
};

const update: Operation<
  { id: number },
  void,
  {
    display_name?: string;
    primary_person_id?: number | null;
    primary_organization_id?: number | null;
    status_slug?: string | null;
    source_slug?: string | null;
    owner_actor_id?: number | null;
  },
  z.infer<typeof crmLeadRowSchema>
> = {
  method: "PATCH",
  path: ({ id }) => `/api/crm/leads/${id}`,
  params: crmIdParams,
  body: z
    .object({
      display_name: z.string().min(1).optional(),
      primary_person_id: z.number().nullable().optional(),
      primary_organization_id: z.number().nullable().optional(),
      status_slug: z.string().nullable().optional(),
      source_slug: z.string().nullable().optional(),
      owner_actor_id: z.number().nullable().optional(),
    })
    .partial(),
  response: crmLeadRowSchema,
  invalidates: [
    list,
    scoped(get, (args: Args<{ id: number }, void, unknown>) => ({
      id: args.params.id,
    })),
  ],
};

const archive: Operation<
  { id: number },
  void,
  { reason?: string | null },
  z.infer<typeof crmLeadRowSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/leads/${id}/archive`,
  params: crmIdParams,
  body: z.object({ reason: z.string().nullable().optional() }),
  response: crmLeadRowSchema,
  invalidates: [
    list,
    scoped(get, (args: Args<{ id: number }, void, unknown>) => ({
      id: args.params.id,
    })),
  ],
};

const bulkUpdate: Operation<
  void,
  void,
  {
    lead_ids: number[];
    patch: {
      status_slug?: string;
      owner_actor_id?: number | null;
      tag_slugs_add?: string[];
      tag_slugs_remove?: string[];
      archive?: boolean;
      enroll_sequence_id?: number;
    };
  },
  { affected: number }
> = {
  method: "POST",
  path: () => "/api/crm/leads/bulk",
  body: z.object({
    lead_ids: z.array(z.number().int()).min(1),
    patch: z.object({
      status_slug: z.string().optional(),
      owner_actor_id: z.number().nullable().optional(),
      tag_slugs_add: z.array(z.string()).optional(),
      tag_slugs_remove: z.array(z.string()).optional(),
      archive: z.boolean().optional(),
      enroll_sequence_id: z.number().int().optional(),
    }),
  }),
  response: z.object({ affected: z.number() }),
  // Multi-id write — wildcards the whole list family rather than N scoped gets.
  invalidates: [list],
};

const addContact: Operation<
  { id: number },
  void,
  { person_id: number; is_primary?: boolean },
  { ok: true }
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/leads/${id}/contacts`,
  params: crmIdParams,
  body: z.object({
    person_id: z.number().int(),
    is_primary: z.boolean().optional(),
  }),
  response: z.object({ ok: z.literal(true) }),
  invalidates: [
    scoped(get, (args: Args<{ id: number }, void, unknown>) => ({
      id: args.params.id,
    })),
    scoped(contactsList, (args: Args<{ id: number }, void, unknown>) => ({
      id: args.params.id,
    })),
  ],
};

interface TimelineRow {
  id: number;
  entity_kind: string;
  entity_id: number;
  verb: string;
  actor_id: number | null;
  actor_label: string | null;
  payload: unknown;
  created_at: string;
}

const timeline: Operation<
  { id: number },
  { limit?: number },
  void,
  { rows: TimelineRow[] }
> = {
  method: "GET",
  path: ({ id }) => `/api/crm/leads/${id}/timeline`,
  params: crmIdParams,
  query: z.object({ limit: z.coerce.number().int().min(1).max(500).optional() }).partial(),
  response: z.custom<{ rows: TimelineRow[] }>(),
};

// invalidation-ignore: write-nothing POST — pure read via applySmartView (no vault write)
const applySmartView: Operation<
  void,
  void,
  { entity_kind?: "crm_lead" | "crm_opportunity"; query: z.infer<typeof smartViewQuerySchema> },
  {
    leads?: z.infer<typeof crmLeadRowSchema>[];
    opportunities?: unknown[];
  }
> = {
  method: "POST",
  path: () => "/api/crm/leads/apply_smart_view",
  body: z.object({
    entity_kind: z.enum(["crm_lead", "crm_opportunity"]).optional(),
    query: smartViewQuerySchema,
  }),
  response: z.object({
    leads: z.array(crmLeadRowSchema).optional(),
    opportunities: z.array(z.unknown()).optional(),
  }),
};

// Bulk EXPORT of leads or opportunities as CSV/JSON via the Response escape
// hatch — the "pair with an existing set of tools" path (download the book of
// business and load it anywhere). The handler returns a raw `Response`, so
// TResult is `unknown` (downloaded via an <a download>, never parsed).
const exportData: Operation<
  void,
  { entity?: "leads" | "opportunities"; format?: "csv" | "json" },
  void,
  unknown
> = {
  method: "GET",
  path: () => "/api/crm/export",
  query: z.object({
    entity: z.enum(["leads", "opportunities"]).optional(),
    format: z.enum(["csv", "json"]).optional(),
  }),
  response: z.unknown(),
};

export const leads = {
  list,
  create,
  get,
  update,
  archive,
  bulk_update: bulkUpdate,
  add_contact: addContact,
  contacts_list: contactsList,
  timeline,
  apply_smart_view: applySmartView,
  export: exportData,
};
